import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  BoltIcon, 
  PuzzlePieceIcon, 
  CodeBracketIcon, 
  ChatBubbleLeftRightIcon, 
  ClockIcon, 
  ShieldCheckIcon 
} from '@heroicons/react/24/outline';

import FeatureCard from '../ui/FeatureCard';

const features = [
  {
    title: 'AI-Powered Automation',
    description: 'Deploy intelligent AI agents that learn from your processes and automate complex workflows across platforms.',
    icon: <BoltIcon className="w-6 h-6" />,
  },
  {
    title: 'No-Code Configuration',
    description: 'Create and customize AI agents without writing a single line of code using our intuitive visual interface.',
    icon: <PuzzlePieceIcon className="w-6 h-6" />,
  },
  {
    title: 'Developer API',
    description: 'Advanced customization with our robust API for developers who want to build custom integrations.',
    icon: <CodeBracketIcon className="w-6 h-6" />,
  },
  {
    title: 'Multi-Channel Communication',
    description: 'Deploy agents across Slack, Discord, WhatsApp, email, and more with consistent behavior.',
    icon: <ChatBubbleLeftRightIcon className="w-6 h-6" />,
  },
  {
    title: 'Real-Time Execution',
    description: 'Agents respond in real-time to user requests and system events, with minimal latency.',
    icon: <ClockIcon className="w-6 h-6" />,
  },
  {
    title: 'Enterprise-Grade Security',
    description: 'SOC 2 compliant with end-to-end encryption and granular permission controls for your data.',
    icon: <ShieldCheckIcon className="w-6 h-6" />,
  },
];

const FeaturesOverviewSection: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section className="py-20 bg-white" id="features">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.span 
            className="text-primary-600 font-semibold tracking-wide uppercase text-sm"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Powerful Features
          </motion.span>
          <motion.h2 
            className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Everything You Need to Automate with AI
          </motion.h2>
          <motion.p 
            className="mt-4 text-xl text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Build, deploy, and manage AI agents across your organization without technical expertise.
          </motion.p>
        </div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              title={feature.title}
              description={feature.description}
              icon={feature.icon}
              delay={index * 0.1}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesOverviewSection;